import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, toast } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Plus, RefreshCw, Trash2, ExternalLink, Clock, AlertCircle, CheckCircle2, Tag, Settings2, Zap, FileText, Eye, Loader2, Globe, Send, RotateCcw, CalendarClock, ScanSearch, Play, Pause, ChevronDown } from 'lucide-react';

// Composants extraits pour améliorer la maintenabilité
import { TYPE_CONFIG, TypeBadge, TypeFilterButton } from '../components/VeillePage';
import SourcesList from '../components/VeillePage/SourcesList';
import ArticlesTable from '../components/VeillePage/ArticlesTable';

/**
 * VeillePage - Page unifiée pour tous les outils de surveillance.
 * Remplace RadarPage, SonarPage et CiblePage.
 * Filtre par type: rss (Radar), page (Sonar), keywords (Cible)
 */

const VeillePage = () => {
  const [sources, setSources] = useState([]);
  const [articles, setArticles] = useState([]);
  const [wpSites, setWpSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSource, setShowAddSource] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedType, setSelectedType] = useState(null); // null = tous
  const [matchedOnly, setMatchedOnly] = useState(true);
  const [daysFilter, setDaysFilter] = useState(30); // Filtre par défaut: 30 jours
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [refreshingSource, setRefreshingSource] = useState(null);
  const [reviewArticle, setReviewArticle] = useState(null);
  const [scrapingId, setScrapingId] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);
  const [publishingId, setPublishingId] = useState(null);
  const [serviceStatus, setServiceStatus] = useState({ running: false, enabled: false });
  const [togglingService, setTogglingService] = useState(false);
  const [stats, setStats] = useState(null);
  
  // États pour le workflow de test de source (Solution A)
  const [addStep, setAddStep] = useState('config'); // 'config' | 'testing' | 'result'
  const [testingSource, setTestingSource] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);
  const [validatedSites, setValidatedSites] = useState({}); // {site_id: article_data} - sites déjà validés
  
  // États pour le test de scraping
  const [testingScrape, setTestingScrape] = useState(false);
  const [scrapeResult, setScrapeResult] = useState(null);
  
  // États pour le review d'article multisite
  const [selectedSiteTab, setSelectedSiteTab] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [testPrompt, setTestPrompt] = useState('');

  const emptyForm = {
    type: 'rss',
    name: '',
    url: '',
    keywords: '',
    refresh_interval_minutes: 60,
    scheduled_time: '',
    scheduled_days: [],
    target_sites: [],
    custom_prompt: '',
    auto_process: false,
    auto_publish_status: 'draft',
    author_mapping: {},
    title_prompt_mapping: {},
    site_prompts: {},  // Prompts spécifiques par site
    showPerSitePrompts: false,  // Toggle pour afficher/masquer
    image_mode: 'none',
    custom_image_url: '',
    custom_image_urls: {},
    alert_emails: '',
    action_mode: 'email',
    publish_delay_minutes: 0,
    content_mode: 'text',
    smart_scheduling: false,
  };
  const [newForm, setNewForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyForm);

  const loadSources = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedType) params.append('source_type', selectedType);
      const resp = await axios.get(`${API}/watch/sources?${params}`);
      setSources(resp.data);
    } catch (e) {
      console.error('Error loading sources:', e);
    }
  }, [selectedType]);

  const loadArticles = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedSource) params.append('source_id', selectedSource);
      if (selectedType) params.append('source_type', selectedType);
      if (matchedOnly) params.append('matches_only', 'true');
      params.append('days', daysFilter.toString());
      params.append('limit', '200');
      const resp = await axios.get(`${API}/watch/articles?${params}`);
      setArticles(resp.data);
    } catch (e) {
      console.error('Error loading articles:', e);
    }
  }, [selectedSource, selectedType, matchedOnly, daysFilter]);

  const loadWpSites = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/wordpress-sites`);
      setWpSites(resp.data);
    } catch (e) {
      console.error('Error loading WP sites:', e);
    }
  }, []);

  const loadServiceStatus = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/watch/service/status`);
      setServiceStatus(resp.data);
    } catch (e) {
      console.error('Error loading service status:', e);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/watch/stats`);
      setStats(resp.data);
    } catch (e) {
      console.error('Error loading stats:', e);
    }
  }, []);

  useEffect(() => {
    Promise.all([loadSources(), loadArticles(), loadWpSites(), loadServiceStatus(), loadStats()])
      .finally(() => setLoading(false));
  }, [loadSources, loadArticles, loadWpSites, loadServiceStatus, loadStats]);

  useEffect(() => {
    loadSources();
    loadArticles();
  }, [selectedType, selectedSource, matchedOnly, daysFilter, loadSources, loadArticles]);

  // ==================== SERVICE CONTROLS ====================
  const toggleService = async () => {
    setTogglingService(true);
    try {
      if (serviceStatus.running) {
        await axios.post(`${API}/watch/service/stop`);
        toast.success('Service de surveillance arrêté');
      } else {
        await axios.post(`${API}/watch/service/start`);
        toast.success('Service de surveillance démarré');
      }
      await loadServiceStatus();
    } catch (e) {
      toast.error('Erreur de contrôle du service');
    } finally {
      setTogglingService(false);
    }
  };

  // ==================== CRUD ====================
  // Tester une configuration de source AVANT création (version async avec polling)
  // Supporte le test multisite avec validation indépendante
  const handleTestSource = async (retestSitesOnly = null) => {
    if (!newForm.name || !newForm.url) {
      toast.error('Nom et URL requis');
      return;
    }
    if (!newForm.target_sites || newForm.target_sites.length === 0) {
      toast.error('Sélectionnez au moins un site cible');
      return;
    }
    
    setTestingSource(true);
    setTestError(null);
    // Ne pas effacer testResult si on fait un re-test partiel
    if (!retestSitesOnly) {
      setTestResult(null);
    }
    setAddStep('testing');
    
    try {
      // Déterminer quels sites tester (tous ou seulement certains en re-test)
      const sitesToTest = retestSitesOnly || newForm.target_sites;
      const sitesToSkip = retestSitesOnly 
        ? newForm.target_sites.filter(s => !retestSitesOnly.includes(s))
        : Object.keys(validatedSites);
      
      const payload = {
        type: newForm.type,
        url: newForm.url,
        keywords: newForm.keywords.split(',').map(k => k.trim()).filter(Boolean),
        target_sites: sitesToTest,
        custom_prompt: newForm.custom_prompt,
        site_prompts: newForm.site_prompts || {},  // Prompts spécifiques par site
        image_mode: newForm.image_mode,
        custom_image_url: newForm.custom_image_url,
        content_mode: newForm.content_mode || 'text',
        skip_sites: sitesToSkip,  // Sites déjà validés à ne pas régénérer
      };
      
      // Lancer le test en mode async pour éviter les timeouts Cloudflare
      const startResp = await axios.post(`${API}/watch/test-source-async`, payload);
      const testId = startResp.data.test_id;
      
      // Polling toutes les 3 secondes pendant max 5 minutes (plus long pour multisite)
      let attempts = 0;
      const maxAttempts = 100; // 100 * 3s = 5 minutes
      
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        attempts++;
        
        try {
          const statusResp = await axios.get(`${API}/watch/test-source-status/${testId}`);
          
          if (statusResp.data.status === 'done') {
            const result = statusResp.data.result;
            
            // Fusionner les nouveaux résultats avec les sites déjà validés
            if (result.generated_articles) {
              // Ajouter les sites validés précédemment
              for (const [siteId, articleData] of Object.entries(validatedSites)) {
                if (!result.generated_articles[siteId]) {
                  result.generated_articles[siteId] = { ...articleData, validated: true };
                }
              }
            }
            
            if (result.success || Object.keys(result.generated_articles || {}).length > 0) {
              setTestResult(result);
            } else {
              setTestError(result.error || 'Erreur lors du test');
            }
            setAddStep('result');
            setTestingSource(false);
            return;
          }
        } catch (pollError) {
          console.warn('[VeillePage] Polling error (non-blocking):', pollError.message);
        }
      }
      
      setTestError('Timeout: la génération a pris plus de 5 minutes. Réessayez avec moins de sites.');
      setAddStep('result');
      
    } catch (e) {
      let errorMsg = e.response?.data?.detail || e.message || 'Erreur lors du test';
      setTestError(errorMsg);
      setAddStep('result');
    } finally {
      setTestingSource(false);
    }
  };
  
  // Valider un site individuellement
  const handleValidateSite = (siteId) => {
    if (testResult?.generated_articles?.[siteId]) {
      const articleData = testResult.generated_articles[siteId];
      setValidatedSites(prev => ({
        ...prev,
        [siteId]: { ...articleData, validated: true }
      }));
      // Mettre à jour testResult pour marquer comme validé
      setTestResult(prev => ({
        ...prev,
        generated_articles: {
          ...prev.generated_articles,
          [siteId]: { ...prev.generated_articles[siteId], validated: true }
        }
      }));
      toast.success(`Article pour ${articleData.site_name} validé`);
    }
  };
  
  // Retester uniquement les sites non validés
  const handleRetestNonValidated = () => {
    const nonValidatedSites = newForm.target_sites.filter(
      siteId => !validatedSites[siteId] && !testResult?.generated_articles?.[siteId]?.validated
    );
    if (nonValidatedSites.length === 0) {
      toast.info('Tous les sites sont déjà validés');
      return;
    }
    handleTestSource(nonValidatedSites);
  };
  
  // Retester un site spécifique
  const handleRetestSite = (siteId) => {
    // Retirer de la liste des validés
    setValidatedSites(prev => {
      const newState = { ...prev };
      delete newState[siteId];
      return newState;
    });
    handleTestSource([siteId]);
  };

  // Test de scraping seul (sans génération LLM)
  const handleTestScrape = async () => {
    if (!newForm.url) {
      toast.error('URL requise pour tester le scraping');
      return;
    }
    
    setTestingScrape(true);
    setScrapeResult(null);
    
    try {
      const payload = {
        type: newForm.type,
        url: newForm.url,
        keywords: newForm.keywords ? newForm.keywords.split(',').map(k => k.trim()).filter(Boolean) : [],
        content_mode: newForm.content_mode || 'text',
      };
      
      const resp = await axios.post(`${API}/watch/test-scrape`, payload);
      setScrapeResult(resp.data);
      
      if (resp.data.success) {
        toast.success('Scraping réussi');
      } else {
        toast.error(resp.data.error || 'Erreur de scraping');
      }
    } catch (e) {
      setScrapeResult({ success: false, error: e.response?.data?.detail || e.message });
      toast.error('Erreur lors du test de scraping');
    } finally {
      setTestingScrape(false);
    }
  };

  // Régénérer le test avec un nouveau prompt
  const handleRetestSource = async () => {
    await handleTestSource();
  };

  // Valider et créer la source après test réussi
  const handleValidateAndCreate = async () => {
    try {
      // Mapper site_prompts vers title_prompt_mapping (nom backend)
      const sitePromptsMapping = {};
      if (newForm.site_prompts) {
        for (const [siteId, prompt] of Object.entries(newForm.site_prompts)) {
          if (prompt && prompt.trim()) {
            sitePromptsMapping[siteId] = prompt.trim();
          }
        }
      }
      
      const payload = {
        ...newForm,
        keywords: newForm.keywords.split(',').map(k => k.trim()).filter(Boolean),
        refresh_interval_minutes: parseInt(newForm.refresh_interval_minutes) || 60,
        alert_emails: newForm.alert_emails.split(',').map(e => e.trim()).filter(Boolean),
        title_prompt_mapping: sitePromptsMapping,  // Prompts par site
        publish_delay_minutes: parseInt(newForm.publish_delay_minutes) || 0,
      };
      // Supprimer les champs UI temporaires
      delete payload.site_prompts;
      delete payload.showPerSitePrompts;
      
      await axios.post(`${API}/watch/sources`, payload);
      toast.success(`"${newForm.name}" créé avec succès`);
      setNewForm(emptyForm);
      setShowAddSource(false);
      setAddStep('config');
      setTestResult(null);
      setTestError(null);
      await loadSources();
      await loadStats();
      setTimeout(() => loadArticles(), 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création');
    }
  };

  // Fermer le dialog et réinitialiser
  const handleCloseAddDialog = () => {
    setShowAddSource(false);
    setAddStep('config');
    setTestResult(null);
    setTestError(null);
    setValidatedSites({});
    setScrapeResult(null);  // Reset du test de scraping
    setNewForm(emptyForm);
  };

  const handleAddSource = async () => {
    if (!newForm.name || !newForm.url) {
      toast.error('Nom et URL requis');
      return;
    }
    try {
      const payload = {
        ...newForm,
        keywords: newForm.keywords.split(',').map(k => k.trim()).filter(Boolean),
        refresh_interval_minutes: parseInt(newForm.refresh_interval_minutes) || 60,
        alert_emails: newForm.alert_emails.split(',').map(e => e.trim()).filter(Boolean),
      };
      await axios.post(`${API}/watch/sources`, payload);
      toast.success(`"${newForm.name}" ajouté`);
      setNewForm(emptyForm);
      setShowAddSource(false);
      await loadSources();
      await loadStats();
      setTimeout(() => loadArticles(), 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur ajout');
    }
  };

  const handleDeleteSource = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/watch/sources/${confirmDelete.id}`);
      toast.success(`"${confirmDelete.name}" supprimé`);
      if (selectedSource === confirmDelete.id) setSelectedSource(null);
      setConfirmDelete(null);
      await loadSources();
      await loadArticles();
      await loadStats();
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  const openEditSource = (source) => {
    setEditForm({
      type: source.type || 'rss',
      name: source.name || '',
      url: source.url || '',
      keywords: (source.keywords || []).join(', '),
      refresh_interval_minutes: source.refresh_interval_minutes || 60,
      scheduled_time: source.scheduled_time || '',
      scheduled_days: source.scheduled_days || [],
      target_sites: source.target_sites || [],
      custom_prompt: source.custom_prompt || '',
      auto_process: source.auto_process || false,
      auto_publish_status: source.auto_publish_status || 'draft',
      author_mapping: source.author_mapping || {},
      title_prompt_mapping: source.title_prompt_mapping || {},
      site_prompts: source.title_prompt_mapping || {},  // Charger les prompts par site
      showPerSitePrompts: Object.keys(source.title_prompt_mapping || {}).length > 0,
      image_mode: source.image_mode || 'none',
      custom_image_url: source.custom_image_url || '',
      custom_image_urls: source.custom_image_urls || {},
      alert_emails: (source.alert_emails || []).join(', '),
      action_mode: source.action_mode || 'email',
      publish_delay_minutes: source.publish_delay_minutes || 0,
      content_mode: source.content_mode || 'text',
      smart_scheduling: source.smart_scheduling || false,
    });
    setEditingSource(source);
  };

  const handleSaveEdit = async () => {
    if (!editingSource) return;
    try {
      // Mapper site_prompts vers title_prompt_mapping
      const sitePromptsMapping = {};
      if (editForm.site_prompts) {
        for (const [siteId, prompt] of Object.entries(editForm.site_prompts)) {
          if (prompt && prompt.trim()) {
            sitePromptsMapping[siteId] = prompt.trim();
          }
        }
      }
      
      const payload = {
        ...editForm,
        keywords: editForm.keywords.split(',').map(k => k.trim()).filter(Boolean),
        refresh_interval_minutes: parseInt(editForm.refresh_interval_minutes) || 60,
        alert_emails: editForm.alert_emails.split(',').map(e => e.trim()).filter(Boolean),
        title_prompt_mapping: sitePromptsMapping,
        publish_delay_minutes: parseInt(editForm.publish_delay_minutes) || 0,
      };
      // Supprimer les champs UI temporaires
      delete payload.site_prompts;
      delete payload.showPerSitePrompts;
      
      await axios.put(`${API}/watch/sources/${editingSource.id}`, payload);
      toast.success('Mis à jour');
      setEditingSource(null);
      await loadSources();
    } catch (e) {
      toast.error('Erreur mise à jour');
    }
  };

  const handleRefreshSource = async (sourceId) => {
    setRefreshingSource(sourceId);
    try {
      const res = await axios.post(`${API}/watch/sources/${sourceId}/fetch`);
      const data = res.data;
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success(`${data.new || 0} nouveaux articles`);
      }
      await loadSources();
      await loadArticles();
    } catch (e) {
      toast.error('Erreur rafraîchissement');
    } finally {
      setRefreshingSource(null);
    }
  };

  const handleRefreshAll = async () => {
    setRefreshingAll(true);
    try {
      const res = await axios.post(`${API}/watch/fetch-all`);
      toast.success(`${res.data.success}/${res.data.total} sources scannées`);
      await loadSources();
      await loadArticles();
    } catch (e) {
      toast.error('Erreur rafraîchissement');
    } finally {
      setRefreshingAll(false);
    }
  };

  // ==================== ARTICLE ACTIONS ====================
  const handleScrape = async (articleId) => {
    setScrapingId(articleId);
    try {
      await axios.post(`${API}/watch/articles/${articleId}/scrape`);
      toast.success('Contenu scrapé');
      if (reviewArticle?.id === articleId) {
        const resp = await axios.get(`${API}/watch/articles/${articleId}`);
        setReviewArticle(resp.data);
      }
      await loadArticles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur scraping');
    } finally {
      setScrapingId(null);
    }
  };

  const handleGenerate = async (articleId, customPromptOverride = null) => {
    setGeneratingId(articleId);
    try {
      const article = reviewArticle?.id === articleId ? reviewArticle : articles.find(a => a.id === articleId);
      const src = sources.find(s => s.id === article?.source_id);
      // Utiliser le prompt personnalisé si fourni, sinon celui de la source
      const promptToUse = customPromptOverride !== null ? customPromptOverride : (src?.custom_prompt || '');
      await axios.post(`${API}/watch/articles/${articleId}/generate`, {
        target_sites: src?.target_sites || [],
        custom_prompt: promptToUse,
        use_html: src?.content_mode === 'html',
      });
      toast.success('Article généré');
      if (reviewArticle?.id === articleId) {
        const resp = await axios.get(`${API}/watch/articles/${articleId}`);
        setReviewArticle(resp.data);
        // Sélectionner le premier site par défaut
        const genArticles = resp.data.generated_articles || {};
        const firstSiteId = Object.keys(genArticles)[0];
        if (firstSiteId) setSelectedSiteTab(firstSiteId);
      }
      await loadArticles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur génération');
    } finally {
      setGeneratingId(null);
    }
  };

  // Régénérer avec le prompt de test (reset + generate)
  const handleRegenerate = async () => {
    if (!reviewArticle) return;
    setRegenerating(true);
    try {
      // Reset l'article pour supprimer la génération précédente
      await axios.post(`${API}/watch/articles/${reviewArticle.id}/reset`);
      // Régénérer avec le prompt de test
      await handleGenerate(reviewArticle.id, testPrompt);
      toast.success('Article régénéré avec le nouveau prompt');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur régénération');
    } finally {
      setRegenerating(false);
    }
  };

  const openReview = async (articleId) => {
    try {
      const resp = await axios.get(`${API}/watch/articles/${articleId}`);
      setReviewArticle(resp.data);
      // Charger le prompt de la source pour le mode test
      const src = sources.find(s => s.id === resp.data.source_id);
      setTestPrompt(src?.custom_prompt || '');
      // Sélectionner le premier site si des articles sont générés
      const genArticles = resp.data.generated_articles || {};
      const firstSiteId = Object.keys(genArticles)[0] || (resp.data.target_sites?.[0]);
      setSelectedSiteTab(firstSiteId || null);
    } catch (e) {
      toast.error('Erreur chargement');
    }
  };

  const handlePublish = async (articleId, publishStatus) => {
    setPublishingId(articleId);
    try {
      const article = reviewArticle || articles.find(a => a.id === articleId);
      const targetSites = article?.target_sites || [];
      if (!targetSites.length) {
        toast.error('Aucun site cible configuré');
        setPublishingId(null);
        return;
      }
      const res = await axios.post(`${API}/watch/articles/${articleId}/publish`, {
        publish_status: publishStatus,
        target_sites: targetSites,
      });
      const siteNames = targetSites.map(sid => wpSites.find(s => s.id === sid)?.name || sid).join(', ');
      if (res.data.success) {
        toast.success(`${publishStatus === 'draft' ? 'Brouillon' : 'Article'} publié sur ${siteNames}`);
      } else {
        toast.error('Échec de publication');
      }
      if (reviewArticle?.id === articleId) {
        const resp = await axios.get(`${API}/watch/articles/${articleId}`);
        setReviewArticle(resp.data);
      }
      await loadArticles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur publication');
    } finally {
      setPublishingId(null);
    }
  };

  const handleDeleteArticle = async (articleId) => {
    try {
      await axios.delete(`${API}/watch/articles/${articleId}`);
      toast.success('Article supprimé');
      if (reviewArticle?.id === articleId) setReviewArticle(null);
      await loadArticles();
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  const handleResetArticle = async (articleId) => {
    try {
      await axios.post(`${API}/watch/articles/${articleId}/reset`);
      toast.success('Article réinitialisé');
      if (reviewArticle?.id === articleId) {
        const resp = await axios.get(`${API}/watch/articles/${articleId}`);
        setReviewArticle(resp.data);
      }
      await loadArticles();
    } catch (e) {
      toast.error('Erreur réinitialisation');
    }
  };

  // ==================== RENDERERS ====================
  const renderSiteSelector = (value, onChange) => (
    <div className="flex flex-wrap gap-2 mt-1">
      {wpSites.map(site => {
        const selected = value.includes(site.id);
        return (
          <button
            key={site.id}
            type="button"
            onClick={() => onChange(selected ? value.filter(id => id !== site.id) : [...value, site.id])}
            className={`text-xs font-mono px-2 py-1 rounded border transition-all ${selected ? 'bg-sky-500/20 border-sky-500 text-sky-300' : 'bg-slate-900/50 border-slate-700 text-slate-500 hover:border-slate-500'}`}
            data-testid={`site-select-${site.id}`}
          >
            {site.name}
          </button>
        );
      })}
    </div>
  );

  const renderSourceForm = (form, setForm, prefix) => {
    const typeConfig = TYPE_CONFIG[form.type] || TYPE_CONFIG.rss;
    const isKeywords = form.type === 'keywords';
    const isPage = form.type === 'page';

    return (
      <div className="space-y-4">
        {/* Type de source */}
        <div>
          <label className="text-sm text-slate-400 uppercase tracking-wider">Type de source</label>
          <div className="flex gap-2 mt-1">
            {Object.entries(TYPE_CONFIG).map(([typeKey, cfg]) => {
              const Icon = cfg.icon;
              const selected = form.type === typeKey;
              return (
                <button
                  key={typeKey}
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, type: typeKey }))}
                  className={`flex items-center gap-2 text-xs font-mono px-3 py-2 rounded border transition-all ${
                    selected
                      ? `bg-${cfg.color}-500/20 border-${cfg.color}-500 text-${cfg.color}-300`
                      : 'bg-slate-900/50 border-slate-700 text-slate-500 hover:border-slate-500'
                  }`}
                  style={selected ? { backgroundColor: `rgba(var(--${cfg.color}-500), 0.2)` } : {}}
                  data-testid={`${prefix}-type-${typeKey}`}
                >
                  <Icon className="w-4 h-4" />
                  {cfg.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-sm text-slate-400 uppercase tracking-wider">Nom de la source</label>
          <Input
            value={form.name}
            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Ex: Le Monde Économie"
            className="mt-1 bg-slate-900/50 border-slate-700 text-slate-200"
            data-testid={`${prefix}-source-name`}
          />
        </div>

        <div>
          <label className="text-sm text-slate-400 uppercase tracking-wider">{typeConfig.sourcesLabel} - URL</label>
          <Input
            value={form.url}
            onChange={(e) => setForm(prev => ({ ...prev, url: e.target.value }))}
            placeholder={typeConfig.urlPlaceholder}
            className="mt-1 bg-slate-900/50 border-slate-700 text-slate-200"
            data-testid={`${prefix}-source-url`}
          />
        </div>

        <div>
          <label className="text-sm text-slate-400 uppercase tracking-wider">
            {isKeywords ? 'Mots-clés à détecter (obligatoire)' : 'Mots-clés de filtrage (optionnel)'}
          </label>
          <Input
            value={form.keywords}
            onChange={(e) => setForm(prev => ({ ...prev, keywords: e.target.value }))}
            placeholder="inflation, BCE, taux"
            className="mt-1 bg-slate-900/50 border-slate-700 text-slate-200"
            data-testid={`${prefix}-source-keywords`}
          />
          <p className="text-xs text-slate-600 mt-1">Séparés par des virgules</p>
        </div>

        {/* Planification */}
        <div>
          <label className="text-sm text-slate-400 uppercase tracking-wider">Planification du scan</label>
          <div className="flex gap-2 mt-1">
            <button
              type="button"
              onClick={() => setForm(prev => ({ ...prev, scheduled_time: '' }))}
              className={`text-xs font-mono px-3 py-1.5 rounded border transition-all ${!form.scheduled_time ? 'bg-sky-500/20 border-sky-500 text-sky-300' : 'bg-slate-900/50 border-slate-700 text-slate-500 hover:border-slate-500'}`}
              data-testid={`${prefix}-mode-interval`}
            >
              Intervalle
            </button>
            <button
              type="button"
              onClick={() => setForm(prev => ({ ...prev, scheduled_time: prev.scheduled_time || '08:00' }))}
              className={`text-xs font-mono px-3 py-1.5 rounded border transition-all ${form.scheduled_time ? 'bg-sky-500/20 border-sky-500 text-sky-300' : 'bg-slate-900/50 border-slate-700 text-slate-500 hover:border-slate-500'}`}
              data-testid={`${prefix}-mode-scheduled`}
            >
              Heure fixe
            </button>
          </div>
          {!form.scheduled_time ? (
            <div className="mt-2">
              <label className="text-xs text-slate-500">Toutes les X minutes</label>
              <Input
                type="number"
                value={form.refresh_interval_minutes}
                onChange={(e) => setForm(prev => ({ ...prev, refresh_interval_minutes: parseInt(e.target.value) || 60 }))}
                min={5}
                className="mt-1 bg-slate-900/50 border-slate-700 text-slate-200 w-32"
                data-testid={`${prefix}-source-interval`}
              />
            </div>
          ) : (
            <div className="mt-2 space-y-2">
              <label className="text-xs text-slate-500">Heure de scan (UTC)</label>
              <Input
                type="time"
                value={form.scheduled_time}
                onChange={(e) => setForm(prev => ({ ...prev, scheduled_time: e.target.value }))}
                className="bg-slate-900/50 border-slate-700 text-slate-200 w-40"
                data-testid={`${prefix}-source-scheduled-time`}
              />
              <div className="flex gap-1.5">
                {[
                  { day: 0, label: 'L' },
                  { day: 1, label: 'Ma' },
                  { day: 2, label: 'Me' },
                  { day: 3, label: 'J' },
                  { day: 4, label: 'V' },
                  { day: 5, label: 'S' },
                  { day: 6, label: 'D' },
                ].map(({ day, label }) => {
                  const isSelected = (form.scheduled_days || []).includes(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => {
                        setForm(prev => {
                          const days = prev.scheduled_days || [];
                          return { ...prev, scheduled_days: isSelected ? days.filter(d => d !== day) : [...days, day].sort() };
                        });
                      }}
                      className={`w-8 h-8 rounded text-xs font-mono font-bold transition-all ${
                        isSelected
                          ? 'bg-sky-500/30 border-sky-500 text-sky-300 border'
                          : 'bg-slate-900/50 border-slate-700 text-slate-500 border hover:border-slate-500'
                      }`}
                      data-testid={`${prefix}-day-${day}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Actions pour Cible */}
        {isKeywords && (
          <div>
            <label className="text-sm text-slate-400 uppercase tracking-wider">Action sur détection</label>
            <div className="flex gap-2 mt-1">
              {[
                { value: 'email', label: 'Alerte email', cls: 'bg-blue-500/20 border-blue-500 text-blue-300' },
                { value: 'write', label: 'Écriture auto', cls: 'bg-amber-500/20 border-amber-500 text-amber-300' },
                { value: 'both', label: 'Les deux', cls: 'bg-emerald-500/20 border-emerald-500 text-emerald-300' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, action_mode: opt.value }))}
                  className={`text-xs font-mono px-3 py-1.5 rounded border transition-all ${form.action_mode === opt.value ? opt.cls : 'bg-slate-900/50 border-slate-700 text-slate-500 hover:border-slate-500'}`}
                  data-testid={`${prefix}-action-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Emails d'alerte */}
        {isKeywords && (form.action_mode === 'email' || form.action_mode === 'both') && (
          <div>
            <label className="text-sm text-slate-400 uppercase tracking-wider">Adresses email d&#39;alerte</label>
            <Input
              value={form.alert_emails}
              onChange={(e) => setForm(prev => ({ ...prev, alert_emails: e.target.value }))}
              placeholder="alerte@example.com, autre@example.com"
              className="mt-1 bg-slate-900/50 border-slate-700 text-slate-200"
              data-testid={`${prefix}-alert-emails`}
            />
          </div>
        )}

        {/* Sites cibles */}
        <div>
          <label className="text-sm text-slate-400 uppercase tracking-wider">Sites cibles pour publication</label>
          {renderSiteSelector(form.target_sites, (v) => setForm(prev => ({ ...prev, target_sites: v })))}
        </div>

        {/* Délai de publication multisite (visible si 2+ sites) */}
        {form.target_sites?.length > 1 && (
          <div className="p-3 bg-slate-900/30 border border-slate-700 rounded-lg">
            <label className="text-sm text-slate-400 uppercase tracking-wider flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Délai entre publications (multisite)
            </label>
            <div className="flex items-center gap-3 mt-2">
              <input
                type="number"
                min="0"
                max="1440"
                value={form.publish_delay_minutes || 0}
                onChange={(e) => setForm(prev => ({ ...prev, publish_delay_minutes: parseInt(e.target.value) || 0 }))}
                className="w-20 bg-slate-900/50 border border-slate-700 text-slate-200 rounded-md px-3 py-1.5 text-sm font-mono focus:border-sky-500 focus:outline-none"
                data-testid={`${prefix}-delay-minutes`}
              />
              <span className="text-sm text-slate-500">minutes entre chaque site</span>
            </div>
            <p className="text-xs text-slate-600 mt-1">
              Ex: 15 min → Site 1 immédiat, Site 2 à +15min, Site 3 à +30min...
            </p>
          </div>
        )}

        {/* Prompt personnalisé global */}
        <div>
          <label className="text-sm text-slate-400 uppercase tracking-wider">Prompt personnalisé (global)</label>
          <textarea
            value={form.custom_prompt}
            onChange={(e) => setForm(prev => ({ ...prev, custom_prompt: e.target.value }))}
            placeholder="Instructions spécifiques pour la génération (appliquées à tous les sites)..."
            className="mt-1 w-full bg-slate-900/50 border border-slate-700 text-slate-200 rounded-md px-3 py-2 text-sm font-mono min-h-[80px] focus:border-sky-500 focus:outline-none"
            data-testid={`${prefix}-source-prompt`}
          />
        </div>

        {/* Prompts par site (optionnel, visible si sites sélectionnés) */}
        {form.target_sites?.length > 0 && (
          <div className="p-3 bg-slate-900/30 border border-slate-700 rounded-lg">
            <button
              type="button"
              onClick={() => setForm(prev => ({ ...prev, showPerSitePrompts: !prev.showPerSitePrompts }))}
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-300 transition-colors w-full"
            >
              <ChevronDown className={`w-4 h-4 transition-transform ${form.showPerSitePrompts ? 'rotate-180' : ''}`} />
              <span className="uppercase tracking-wider">Prompts spécifiques par site</span>
              <span className="text-xs text-slate-600 ml-auto">(optionnel)</span>
            </button>
            
            {form.showPerSitePrompts && (
              <div className="mt-3 space-y-3">
                {form.target_sites.map(siteId => {
                  const site = wpSites.find(s => s.id === siteId);
                  if (!site) return null;
                  return (
                    <div key={siteId} className="border-l-2 border-sky-500/30 pl-3">
                      <label className="text-xs text-sky-400 font-mono">{site.name}</label>
                      <textarea
                        value={form.site_prompts?.[siteId] || ''}
                        onChange={(e) => setForm(prev => ({
                          ...prev,
                          site_prompts: { ...prev.site_prompts, [siteId]: e.target.value }
                        }))}
                        placeholder={`Instructions spécifiques pour ${site.name}...`}
                        className="mt-1 w-full bg-slate-900/50 border border-slate-700 text-slate-200 rounded-md px-3 py-2 text-xs font-mono min-h-[60px] focus:border-sky-500 focus:outline-none"
                        data-testid={`${prefix}-site-prompt-${siteId}`}
                      />
                    </div>
                  );
                })}
                <p className="text-xs text-slate-600">
                  Ces prompts s&apos;ajoutent au prompt global pour chaque site spécifique.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Image */}
        <div>
          <label className="text-sm text-slate-400 uppercase tracking-wider">Image d&#39;illustration</label>
          <div className="flex gap-2 mt-2">
            {[
              { value: 'none', label: 'Aucune', cls: 'bg-slate-500/20 border-slate-500 text-slate-300' },
              { value: 'ai', label: 'IA', cls: 'bg-purple-500/20 border-purple-500 text-purple-300' },
              { value: 'custom', label: 'Personnalisée', cls: 'bg-amber-500/20 border-amber-500 text-amber-300' },
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setForm(prev => ({ ...prev, image_mode: opt.value }))}
                className={`text-xs font-mono px-3 py-1.5 rounded border transition-all ${form.image_mode === opt.value ? opt.cls : 'bg-slate-900/50 border-slate-700 text-slate-500 hover:border-slate-500'}`}
                data-testid={`${prefix}-image-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Auto-process */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setForm(prev => ({ ...prev, auto_process: !prev.auto_process }))}
            className={`w-10 h-5 rounded-full transition-colors ${form.auto_process ? 'bg-sky-500' : 'bg-slate-700'}`}
            data-testid={`${prefix}-source-auto`}
          >
            <div className={`w-4 h-4 rounded-full bg-white transition-transform ${form.auto_process ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
          <span className="text-sm text-slate-400">Traitement automatique (scrape + génération + publication)</span>
        </div>

        {form.auto_process && (
          <div className="ml-13 pl-4 border-l-2 border-sky-500/30 space-y-2">
            <label className="text-sm text-slate-400 uppercase tracking-wider">Mode de publication</label>
            <div className="flex gap-2 flex-wrap">
              {[
                { value: 'draft', label: 'Brouillon', cls: 'bg-amber-500/20 border-amber-500 text-amber-300' },
                { value: 'pending', label: 'En attente', cls: 'bg-blue-500/20 border-blue-500 text-blue-300' },
                { value: 'publish', label: 'En ligne', cls: 'bg-emerald-500/20 border-emerald-500 text-emerald-300' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, auto_publish_status: opt.value }))}
                  className={`text-xs font-mono px-3 py-1.5 rounded border transition-all ${form.auto_publish_status === opt.value ? opt.cls : 'bg-slate-900/50 border-slate-700 text-slate-500 hover:border-slate-500'}`}
                  data-testid={`${prefix}-source-publish-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ==================== RENDER ====================
  return (
    <div className="min-h-screen p-6" data-testid="veille-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1
            className="text-2xl md:text-3xl font-bold tracking-wider uppercase flex items-center gap-3"
            style={{ fontFamily: "'Orbitron', monospace", color: '#00e5ff', textShadow: '0 0 20px rgba(0,229,255,0.3)' }}
          >
            <ScanSearch className="w-8 h-8" />
            SURVEILLANCE
          </h1>
          <p className="text-slate-500 font-mono text-sm">
            {stats ? `${stats.total_sources} sources · ${stats.total_articles} articles` : 'Chargement...'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Service toggle */}
          <button
            onClick={toggleService}
            disabled={togglingService}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border font-mono text-sm transition-all ${
              serviceStatus.running
                ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                : 'bg-slate-900/50 border-slate-700 text-slate-400 hover:border-slate-500'
            }`}
            data-testid="service-toggle-btn"
          >
            {togglingService ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : serviceStatus.running ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            {serviceStatus.running ? 'Actif' : 'Arrêté'}
          </button>
          <Button
            onClick={handleRefreshAll}
            disabled={refreshingAll}
            variant="outline"
            className="border-slate-600 text-slate-300"
            data-testid="refresh-all-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshingAll ? 'animate-spin' : ''}`} />
            Tout scanner
          </Button>
          <Button onClick={() => setShowAddSource(true)} className="bg-sky-600 hover:bg-sky-500 text-white font-semibold shadow-md shadow-sky-600/20" data-testid="add-source-btn">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter
          </Button>
        </div>
      </div>

      {/* Type filters */}
      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={() => { setSelectedType(null); setSelectedSource(null); }}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border font-mono text-sm transition-all ${
            !selectedType ? 'bg-sky-500/20 border-sky-500 text-sky-300' : 'bg-slate-900/50 border-slate-700 text-slate-400 hover:border-slate-500'
          }`}
          data-testid="filter-all"
        >
          <ScanSearch className="w-4 h-4" />
          Tous
          {stats && <span className="text-xs opacity-60">({stats.total_sources})</span>}
        </button>
        {Object.entries(TYPE_CONFIG).map(([typeKey, cfg]) => {
          const Icon = cfg.icon;
          const count = stats?.sources_by_type?.[typeKey] || 0;
          return (
            <button
              key={typeKey}
              onClick={() => { setSelectedType(typeKey); setSelectedSource(null); }}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border font-mono text-sm transition-all ${
                selectedType === typeKey
                  ? `bg-${cfg.color}-500/20 border-${cfg.color}-500 text-${cfg.color}-300`
                  : 'bg-slate-900/50 border-slate-700 text-slate-400 hover:border-slate-500'
              }`}
              data-testid={`filter-${typeKey}`}
            >
              <Icon className="w-4 h-4" />
              {cfg.label}
              <span className="text-xs opacity-60">({count})</span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left - Sources (Composant extrait) */}
        <SourcesList
          sources={sources}
          selectedSource={selectedSource}
          serviceStatus={serviceStatus}
          refreshingSource={refreshingSource}
          loading={loading}
          onSelectSource={setSelectedSource}
          onToggleSource={async (src) => {
            try {
              await axios.post(`${API}/watch/sources/${src.id}/toggle`);
              await loadSources();
              toast.success(src.is_active === false ? `"${src.name}" activée` : `"${src.name}" désactivée`);
            } catch (err) {
              toast.error('Erreur lors du toggle');
            }
          }}
          onEditSource={openEditSource}
          onRefreshSource={handleRefreshSource}
          onDeleteSource={setConfirmDelete}
        />

        {/* Right - Articles (Composant extrait) */}
        <ArticlesTable
          articles={articles}
          sources={sources}
          wpSites={wpSites}
          loading={loading}
          daysFilter={daysFilter}
          setDaysFilter={setDaysFilter}
          matchedOnly={matchedOnly}
          setMatchedOnly={setMatchedOnly}
          scrapingId={scrapingId}
          generatingId={generatingId}
          publishingId={publishingId}
          onScrape={handleScrape}
          onGenerate={handleGenerate}
          onReview={openReview}
          onPublish={handlePublish}
          onReset={handleResetArticle}
          onDelete={handleDeleteArticle}
        />
      </div>

      {/* Dialog: Ajouter source - Workflow en 2 étapes */}
      <Dialog open={showAddSource} onOpenChange={handleCloseAddDialog}>
        <DialogContent className="bg-slate-950 border-sky-500/50 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-sky-400" style={{ fontFamily: "'Orbitron', monospace" }}>
              {addStep === 'config' && 'Étape 1/2 — Configuration'}
              {addStep === 'testing' && 'Test en cours...'}
              {addStep === 'result' && 'Étape 2/2 — Validation'}
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">
              {addStep === 'config' && 'Configurez la source, puis testez avant de créer.'}
              {addStep === 'testing' && 'Récupération et génération d\'un article de test...'}
              {addStep === 'result' && 'Vérifiez le résultat et validez pour créer la source.'}
            </DialogDescription>
          </DialogHeader>

          {/* Étape 1: Configuration */}
          {addStep === 'config' && (
            <>
              {renderSourceForm(newForm, setNewForm, 'input')}
              
              {/* Section Test de Scraping */}
              <div className="mt-4 p-3 bg-slate-900/50 border border-slate-700 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400 uppercase tracking-wider flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Test de scraping
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleTestScrape}
                    disabled={!newForm.url || testingScrape}
                    className="h-7 px-3 text-xs border-sky-500/50 text-sky-400 hover:bg-sky-500/10"
                    data-testid="test-scrape-btn"
                  >
                    {testingScrape ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Eye className="w-3 h-3 mr-1" />}
                    {newForm.type === 'rss' ? 'Analyser le flux' : 'Scraper la page'}
                  </Button>
                </div>
                <p className="text-xs text-slate-600">
                  {newForm.type === 'rss' && 'Voir les derniers articles du flux RSS sans générer de contenu.'}
                  {newForm.type === 'page' && 'Voir le contenu extrait de la page avant génération.'}
                  {newForm.type === 'keywords' && 'Voir le contenu et vérifier les correspondances de mots-clés.'}
                </p>
                
                {/* Résultats du scraping */}
                {scrapeResult && (
                  <div className={`mt-3 p-3 rounded-lg border ${scrapeResult.success ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-red-500/5 border-red-500/30'}`}>
                    {!scrapeResult.success ? (
                      <div className="text-red-400 text-sm">
                        <AlertCircle className="w-4 h-4 inline mr-1" />
                        {scrapeResult.error}
                      </div>
                    ) : (
                      <>
                        {/* Résultats RSS */}
                        {scrapeResult.type === 'rss' && scrapeResult.feed_info && (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-emerald-400 text-sm font-bold">{scrapeResult.feed_info.title}</span>
                              <span className="text-xs text-slate-500">{scrapeResult.feed_info.entries_count} articles</span>
                            </div>
                            <div className="max-h-60 overflow-y-auto space-y-2">
                              {scrapeResult.entries?.map((entry, i) => (
                                <div key={i} className={`p-2 rounded border text-xs ${entry.matches_filter ? 'bg-amber-500/10 border-amber-500/30' : 'bg-slate-800/50 border-slate-700'}`}>
                                  <div className="flex items-start justify-between gap-2">
                                    <a href={entry.link} target="_blank" rel="noopener noreferrer" className="text-slate-200 hover:text-sky-400 font-medium line-clamp-1">
                                      {entry.title}
                                    </a>
                                    {entry.published && <span className="text-slate-600 flex-shrink-0">{entry.published.slice(0, 16)}</span>}
                                  </div>
                                  {entry.description && <p className="text-slate-500 mt-1 line-clamp-2">{entry.description.replace(/<[^>]*>/g, '')}</p>}
                                  {entry.matched_keywords?.length > 0 && (
                                    <div className="flex gap-1 mt-1">
                                      {entry.matched_keywords.map((kw, j) => (
                                        <span key={j} className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded text-[10px]">{kw}</span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Résultats Page/Keywords */}
                        {(scrapeResult.type === 'page' || scrapeResult.type === 'keywords') && scrapeResult.page_info && (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-emerald-400 text-sm font-bold">{scrapeResult.page_info.title}</span>
                              <span className="text-xs text-slate-500">
                                {scrapeResult.page_info.char_count?.toLocaleString()} car. / {scrapeResult.page_info.word_count?.toLocaleString()} mots
                              </span>
                            </div>
                            
                            {/* Analyse mots-clés (Cible) */}
                            {scrapeResult.keyword_analysis && (
                              <div className={`p-2 rounded border ${scrapeResult.keyword_analysis.matches_filter ? 'bg-amber-500/10 border-amber-500/30' : 'bg-slate-800/50 border-slate-700'}`}>
                                <div className="text-xs text-slate-400 mb-1">Mots-clés testés: {scrapeResult.keyword_analysis.keywords_tested.join(', ')}</div>
                                {scrapeResult.keyword_analysis.matches_count > 0 ? (
                                  <div className="flex gap-1 flex-wrap">
                                    <span className="text-amber-400 text-xs mr-1">Trouvés:</span>
                                    {scrapeResult.keyword_analysis.matched_keywords.map((kw, i) => (
                                      <span key={i} className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded text-[10px]">{kw}</span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-slate-500 text-xs">Aucun mot-clé correspondant</span>
                                )}
                              </div>
                            )}
                            
                            {/* Contenu extrait */}
                            <details className="group">
                              <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-400">
                                Voir le contenu extrait ({scrapeResult.content_full_length?.toLocaleString()} caractères)
                              </summary>
                              <div className="mt-2 p-2 bg-slate-900/70 rounded border border-slate-800 text-xs text-slate-400 max-h-40 overflow-y-auto whitespace-pre-wrap font-mono">
                                {scrapeResult.content_preview}
                                {scrapeResult.content_full_length > 3000 && <span className="text-slate-600">... (tronqué)</span>}
                              </div>
                            </details>
                            
                            {/* Liens trouvés (Cible) */}
                            {scrapeResult.links_found?.length > 0 && (
                              <details className="group">
                                <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-400">
                                  Liens trouvés ({scrapeResult.links_found.length})
                                </summary>
                                <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                                  {scrapeResult.links_found.map((link, i) => (
                                    <a key={i} href={link.href} target="_blank" rel="noopener noreferrer" className="block text-[10px] text-sky-400 hover:text-sky-300 truncate">
                                      {link.text || link.href}
                                    </a>
                                  ))}
                                </div>
                              </details>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex gap-3 mt-4">
                <Button variant="outline" onClick={handleCloseAddDialog} className="flex-1 border-slate-600 text-slate-300">
                  Annuler
                </Button>
                <Button 
                  onClick={() => handleTestSource()} 
                  disabled={!newForm.name || !newForm.url || !newForm.target_sites?.length}
                  className="flex-1 bg-sky-600 hover:bg-sky-500 text-white font-semibold"
                  data-testid="test-source-btn"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Tester cette configuration
                </Button>
              </div>
              <p className="text-xs text-slate-600 text-center mt-2">
                Le test récupère un article de la source et génère un aperçu pour validation.
              </p>
            </>
          )}

          {/* Étape intermédiaire: Test en cours */}
          {addStep === 'testing' && (
            <div className="py-12 text-center">
              <Loader2 className="w-12 h-12 animate-spin text-sky-400 mx-auto mb-4" />
              <p className="text-slate-300 font-mono">Génération de l&#39;article en cours...</p>
              <p className="text-slate-500 text-sm mt-2">Scraping + IA Claude (~60-90 secondes)</p>
              <p className="text-slate-600 text-xs mt-4">⚠️ Ne fermez pas cette fenêtre</p>
            </div>
          )}

          {/* Étape 2: Résultat du test */}
          {addStep === 'result' && (
            <div className="space-y-4">
              {/* Erreur globale */}
              {testError && !testResult?.generated_articles && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-red-400 mb-2">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-bold">Échec du test</span>
                  </div>
                  <p className="text-red-300 text-sm">{testError}</p>
                  <div className="flex gap-3 mt-4">
                    <Button variant="outline" onClick={() => setAddStep('config')} className="border-slate-600 text-slate-300">
                      ← Modifier la configuration
                    </Button>
                    <Button onClick={() => handleTestSource()} disabled={testingSource} className="bg-sky-600 hover:bg-sky-500 text-white font-semibold">
                      {testingSource ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                      Réessayer
                    </Button>
                  </div>
                </div>
              )}

              {/* Résultats (succès partiel ou total) */}
              {(testResult?.success || testResult?.generated_articles) && (
                <>
                  {/* Article source (colapsible) */}
                  <details className="group">
                    <summary className="cursor-pointer p-3 bg-slate-900/50 border border-slate-700 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        <span className="text-sm text-slate-300">Article source récupéré</span>
                      </div>
                      <span className="text-xs text-slate-500">Voir détails</span>
                    </summary>
                    <div className="mt-2 p-3 bg-slate-900/30 border border-slate-800 rounded-lg text-sm">
                      <p className="text-slate-200 font-medium">{testResult.source_article?.title}</p>
                      <a href={testResult.source_article?.link} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-400 hover:underline">
                        {testResult.source_article?.link}
                      </a>
                      {testResult.scraped_content && (
                        <p className="text-xs text-slate-500 mt-2">{testResult.scraped_content.char_count?.toLocaleString()} caractères extraits</p>
                      )}
                    </div>
                  </details>

                  {/* Image générée par IA (si présente) */}
                  {testResult.generated_image && (
                    <div className="p-3 bg-slate-900/50 border border-purple-500/30 rounded-lg">
                      <div className="flex items-center gap-2 text-purple-400 text-xs uppercase tracking-wider mb-2">
                        <Eye className="w-4 h-4" />
                        Image générée (IA)
                      </div>
                      <img src={testResult.generated_image} alt="Image IA" className="w-full max-h-32 object-cover rounded-lg" />
                    </div>
                  )}

                  {/* Compteur de validation */}
                  {Object.keys(testResult.generated_articles || {}).length > 1 && (
                    <div className="p-3 bg-slate-800/50 border border-slate-700 rounded-lg flex items-center justify-between">
                      <div className="text-sm text-slate-400">
                        <span className="text-emerald-400 font-bold">
                          {Object.values(testResult.generated_articles || {}).filter(a => a.validated).length}
                        </span>
                        <span className="mx-1">/</span>
                        <span>{Object.keys(testResult.generated_articles || {}).length}</span>
                        <span className="ml-2">sites validés</span>
                      </div>
                      {Object.values(testResult.generated_articles || {}).some(a => !a.validated && !a.error) && (
                        <Button
                          size="sm"
                          onClick={handleRetestNonValidated}
                          disabled={testingSource}
                          className="h-7 px-3 text-xs bg-sky-600 hover:bg-sky-500 text-white"
                        >
                          {testingSource ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                          Régénérer non validés
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Articles générés par site */}
                  {Object.entries(testResult.generated_articles || {}).map(([siteId, article]) => {
                    const isValidated = article.validated || validatedSites[siteId];
                    const hasError = !!article.error;
                    
                    return (
                      <div 
                        key={siteId} 
                        className={`p-4 rounded-lg border ${
                          isValidated 
                            ? 'bg-emerald-500/5 border-emerald-500/30' 
                            : hasError
                              ? 'bg-red-500/5 border-red-500/30'
                              : 'bg-slate-900/50 border-slate-700'
                        }`}
                      >
                        {/* En-tête du site */}
                        <div className="flex items-center justify-between mb-3">
                          <h3 className={`text-sm uppercase tracking-wider font-bold flex items-center gap-2 ${
                            isValidated ? 'text-emerald-400' : hasError ? 'text-red-400' : 'text-sky-400'
                          }`}>
                            {isValidated ? <CheckCircle2 className="w-4 h-4" /> : hasError ? <AlertCircle className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
                            {article.site_name}
                            {isValidated && <span className="text-xs bg-emerald-500/20 px-2 py-0.5 rounded ml-2">Validé</span>}
                          </h3>
                          
                          {/* Boutons d'action par site */}
                          <div className="flex gap-2">
                            {!isValidated && !hasError && (
                              <Button
                                size="sm"
                                onClick={() => handleValidateSite(siteId)}
                                className="h-7 px-3 text-xs bg-emerald-600 hover:bg-emerald-700"
                              >
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Valider
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRetestSite(siteId)}
                              disabled={testingSource}
                              className="h-7 px-3 text-xs border-sky-500/50 text-sky-400 hover:bg-sky-500/10"
                            >
                              {testingSource ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                              {isValidated ? 'Régénérer' : 'Réessayer'}
                            </Button>
                          </div>
                        </div>

                        {/* Erreur spécifique au site */}
                        {hasError && (
                          <p className="text-red-300 text-sm">{article.error}</p>
                        )}

                        {/* Contenu de l'article */}
                        {!hasError && (
                          <div className="space-y-2">
                            <div>
                              <label className="text-xs text-slate-500 uppercase">Titre</label>
                              <p className="text-slate-100 font-bold text-sm">{article.title}</p>
                            </div>
                            {article.summary && (
                              <div>
                                <label className="text-xs text-slate-500 uppercase">Chapô</label>
                                <p className="text-slate-300 text-xs italic border-l-2 border-slate-600 pl-2">{article.summary}</p>
                              </div>
                            )}
                            <details className="group">
                              <summary className="text-xs text-slate-500 uppercase cursor-pointer hover:text-slate-400">
                                Voir le contenu complet
                              </summary>
                              <div
                                className="prose prose-sm prose-invert max-w-none mt-2 text-slate-300 max-h-40 overflow-y-auto p-2 bg-slate-900/50 rounded border border-slate-800 text-xs"
                                dangerouslySetInnerHTML={{ __html: article.content }}
                              />
                            </details>
                            {article.keywords?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {article.keywords.slice(0, 5).map((kw, i) => (
                                  <span key={i} className="text-[10px] bg-sky-500/15 text-sky-400 px-1.5 py-0.5 rounded">{kw}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Zone de prompt spécifique (si non validé) */}
                        {!isValidated && !hasError && (
                          <div className="mt-3 pt-3 border-t border-slate-700/50">
                            <label className="text-xs text-sky-400 uppercase tracking-wider">Prompt spécifique {article.site_name}</label>
                            <textarea
                              value={newForm.site_prompts?.[siteId] || ''}
                              onChange={(e) => setNewForm(prev => ({
                                ...prev,
                                site_prompts: { ...prev.site_prompts, [siteId]: e.target.value }
                              }))}
                              placeholder={`Instructions pour modifier l'article de ${article.site_name}...`}
                              className="mt-1 w-full bg-slate-900/70 border border-slate-700 text-slate-200 rounded-md px-2 py-1.5 text-xs font-mono min-h-[50px] focus:border-sky-500 focus:outline-none"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Actions finales */}
                  <div className="flex gap-3 pt-2 border-t border-slate-700">
                    <Button variant="outline" onClick={() => setAddStep('config')} className="border-slate-600 text-slate-300">
                      ← Modifier
                    </Button>
                    <Button 
                      onClick={handleValidateAndCreate} 
                      disabled={Object.values(testResult.generated_articles || {}).filter(a => a.validated || validatedSites[a.site_id]).length === 0}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50" 
                      data-testid="validate-create-btn"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Créer la source ({Object.values(testResult.generated_articles || {}).filter(a => a.validated || validatedSites[a.site_id]).length} site{Object.values(testResult.generated_articles || {}).filter(a => a.validated || validatedSites[a.site_id]).length > 1 ? 's' : ''} validé{Object.values(testResult.generated_articles || {}).filter(a => a.validated || validatedSites[a.site_id]).length > 1 ? 's' : ''})
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Modifier source */}
      <Dialog open={!!editingSource} onOpenChange={(open) => { if (!open) setEditingSource(null); }}>
        <DialogContent className="bg-slate-950 border-sky-500/50 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-sky-400" style={{ fontFamily: "'Orbitron', monospace" }}>
              Modifier la source
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">
              Modifiez les paramètres de la source de surveillance.
            </DialogDescription>
          </DialogHeader>
          {renderSourceForm(editForm, setEditForm, 'edit')}
          <Button onClick={handleSaveEdit} className="w-full bg-sky-600 hover:bg-sky-500 text-white font-semibold mt-4" data-testid="submit-edit-source">
            <Settings2 className="w-4 h-4 mr-2" />
            Enregistrer
          </Button>
        </DialogContent>
      </Dialog>

      {/* Dialog: Confirmer suppression */}
      <Dialog open={!!confirmDelete} onOpenChange={(open) => { if (!open) setConfirmDelete(null); }}>
        <DialogContent className="bg-slate-950 border-red-500/50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-red-400">Supprimer la source</DialogTitle>
            <DialogDescription className="sr-only">Confirmer la suppression de la source</DialogDescription>
          </DialogHeader>
          <p className="text-slate-300 text-sm mt-2">
            Supprimer <strong className="text-white">{confirmDelete?.name}</strong> et tous ses articles ?
          </p>
          <div className="flex gap-3 mt-4 justify-end">
            <Button variant="outline" onClick={() => setConfirmDelete(null)} className="border-slate-600 text-slate-300" data-testid="cancel-delete">
              Annuler
            </Button>
            <Button onClick={handleDeleteSource} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete">
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Panel: Review article - Simplifié (le test se fait au niveau source) */}
      <Dialog open={!!reviewArticle} onOpenChange={(open) => { if (!open) setReviewArticle(null); }}>
        <DialogContent className="bg-slate-950 border-sky-500/50 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-sky-400" style={{ fontFamily: "'Orbitron', monospace" }}>
              Prévisualisation
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">
              Article généré — prêt pour publication
            </DialogDescription>
          </DialogHeader>

          {reviewArticle && (
            <div className="space-y-4 mt-2">
              {/* Source info - collapsible */}
              <details className="group">
                <summary className="cursor-pointer p-3 bg-slate-900/50 border border-slate-700 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ExternalLink className="w-4 h-4 text-sky-400" />
                    <span className="text-sm text-slate-300 font-medium">{reviewArticle.title?.slice(0, 80)}{reviewArticle.title?.length > 80 ? '...' : ''}</span>
                  </div>
                  <span className="text-xs text-slate-500">Voir source</span>
                </summary>
                <div className="mt-2 p-3 bg-slate-900/30 border border-slate-800 rounded-lg">
                  <a href={reviewArticle.link} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-400 hover:underline break-all">
                    {reviewArticle.link}
                  </a>
                </div>
              </details>

              {/* Scrape si nécessaire */}
              {!reviewArticle.scraped_content && (
                <div className="p-4 bg-slate-900/50 border border-slate-700 rounded-lg text-center">
                  <p className="text-slate-400 text-sm mb-3">Le contenu doit être scrapé</p>
                  <Button
                    onClick={() => handleScrape(reviewArticle.id)}
                    disabled={scrapingId === reviewArticle.id}
                    className="bg-sky-600 hover:bg-sky-500 text-white"
                  >
                    {scrapingId === reviewArticle.id ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scraping...</>
                    ) : (
                      <><FileText className="w-4 h-4 mr-2" />Scraper</>
                    )}
                  </Button>
                </div>
              )}

              {/* Générer si pas encore fait */}
              {reviewArticle.scraped_content && !reviewArticle.generated_article && (
                <div className="p-4 bg-slate-900/50 border border-slate-700 rounded-lg text-center">
                  <p className="text-slate-400 text-sm mb-3">Cliquez pour générer l&apos;article</p>
                  <Button
                    onClick={() => handleGenerate(reviewArticle.id)}
                    disabled={generatingId === reviewArticle.id}
                    className="bg-sky-600 hover:bg-sky-500 text-white"
                  >
                    {generatingId === reviewArticle.id ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Génération...</>
                    ) : (
                      <><Zap className="w-4 h-4 mr-2" />Générer</>
                    )}
                  </Button>
                </div>
              )}

              {/* Article généré */}
              {reviewArticle.generated_article && (
                <div className="p-4 bg-slate-900/50 border border-slate-700 rounded-lg space-y-4">
                  {/* Image générée par IA */}
                  {reviewArticle.generated_article.featured_image_url && (
                    <div className="relative">
                      <label className="text-xs text-slate-500 uppercase mb-2 block">Image à la une (IA)</label>
                      <div className="relative rounded-lg overflow-hidden border border-slate-700 bg-slate-900">
                        <img
                          src={reviewArticle.generated_article.featured_image_url}
                          alt="Image générée par IA"
                          className="w-full max-h-64 object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <div className="absolute top-2 right-2 bg-purple-500/80 text-white text-[10px] px-2 py-0.5 rounded font-mono">
                          IA
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Titre */}
                  <div>
                    <label className="text-xs text-slate-500 uppercase">Titre</label>
                    <p className="text-slate-100 font-bold text-lg mt-1">{reviewArticle.generated_article.title}</p>
                  </div>
                  
                  {/* Chapô */}
                  {reviewArticle.generated_article.summary && (
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Chapô</label>
                      <p className="text-slate-300 text-sm mt-1 italic border-l-2 border-sky-500/50 pl-3">{reviewArticle.generated_article.summary}</p>
                    </div>
                  )}
                  
                  {/* Contenu */}
                  <div>
                    <label className="text-xs text-slate-500 uppercase">Contenu</label>
                    <div
                      className="prose prose-sm prose-invert max-w-none mt-2 text-slate-300 max-h-60 overflow-y-auto p-3 bg-slate-900/50 rounded-lg border border-slate-800"
                      dangerouslySetInnerHTML={{ __html: reviewArticle.generated_article.content }}
                    />
                  </div>
                  
                  {/* Mots-clés */}
                  {reviewArticle.generated_article.keywords?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {reviewArticle.generated_article.keywords.map((kw, i) => (
                        <span key={i} className="text-[10px] bg-sky-500/15 text-sky-400 px-2 py-1 rounded font-mono">{kw}</span>
                      ))}
                    </div>
                  )}
                  
                  {/* Actions de publication */}
                  {!reviewArticle.publish_status && (
                    <div className="flex gap-3 pt-4 border-t border-slate-700">
                      <Button
                        onClick={() => handlePublish(reviewArticle.id, 'draft')}
                        disabled={publishingId === reviewArticle.id}
                        className="bg-sky-600 hover:bg-sky-500 text-white flex-1"
                      >
                        {publishingId === reviewArticle.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                        Brouillon
                      </Button>
                      <Button
                        onClick={() => handlePublish(reviewArticle.id, 'publish')}
                        disabled={publishingId === reviewArticle.id}
                        className="bg-emerald-600 hover:bg-emerald-700 flex-1"
                      >
                        {publishingId === reviewArticle.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Globe className="w-4 h-4 mr-2" />}
                        Publier
                      </Button>
                    </div>
                  )}
                  
                  {reviewArticle.publish_status && (
                    <div className="pt-4 border-t border-slate-700 flex items-center gap-2">
                      {reviewArticle.publish_status === 'published' ? (
                        <><CheckCircle2 className="w-4 h-4 text-emerald-400" /><span className="text-emerald-400 font-mono text-sm">Publié</span></>
                      ) : (
                        <><AlertCircle className="w-4 h-4 text-orange-400" /><span className="text-orange-400 font-mono text-sm">Partiel</span></>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VeillePage;
